import { Component } from "@angular/core";
import { Router } from "@angular/router";
import {
  faBars,
  faChevronDown,
  faChevronRight,
  faFolder,
  faGift,
  faGlobe,
  faHome,
  faKey,
  faLock,
  faSignOutAlt,
  faStickyNote,
  faTruck,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import { AuthService } from "../../services/auth.service";
import { Roles } from "../auth/auth.role";
import { AuthToken } from "../auth/auth.token";
import { GroupsEnum } from "./../../enum/groups.enum";

@Component({
  selector: "app-nav-menu",
  templateUrl: "./nav-menu.component.html",
  styleUrls: ["./nav-menu.component.scss"],
})
export class NavMenuComponent {
  public roles = Roles;
  public groups = GroupsEnum;
  public auth: AuthToken = null;
  faKey = faKey;
  faSignOutAlt = faSignOutAlt;
  faBars = faBars;
  faGlobe = faGlobe;
  faHome = faHome;
  faGift = faGift;
  faFolder = faFolder;
  faLock = faLock;
  faStickyNote = faStickyNote;
  faChevronDown = faChevronDown;
  faChevronRight = faChevronRight;
  faUpload = faUpload;
  faTruck = faTruck;

  constructor(private authService: AuthService, private router: Router) {
    this.auth = authService.getAuth();
  }

  canShow(roles: Roles[]): boolean {
    return this.authService.hasRoles(roles);
  }

  canShowGroup(groups: GroupsEnum[]): boolean {
    return this.authService.hasGroup(groups);
  }

  /**
   * @description Check if can create any ticket
   * @returns true if user has the role to create ticket
   */
  canCreateAny(): boolean {
    const roles = [
      Roles.CreateTicketFundQuota,
      Roles.CreateTicketPrivateFixedIncome,
      Roles.CreateTicketPublicFixedIncome,
      Roles.CreateTicketFutures,
      Roles.CreateTicketMargin,
      Roles.CreateTicketContracted,
      Roles.CreateTicketCurrencyTerm,
      Roles.CreateTicketVariableIncome,
      Roles.CreateTicketSwapCetip,
      Roles.CreateTicketExchange,
      Roles.CreateLiabilitiesTicket,
      Roles.CreateTicketStructuredOperations,
      Roles.CreateStructuredOperationOrderTicket,
    ];

    return this.authService.hasRoles(roles);
  }

  canUploadAny(): boolean {
    const roles = [Roles.CreateTicketVariableIncome];

    return this.authService.hasRoles(roles);
  }

  /***
   * @description Check if can view any ticket
   * @returns true if user has roles
   */
  canViewMonitor(): boolean {
    const roles = [
      Roles.ViewMonitorFundQuota,
      Roles.ViewMonitorPrivateFixedIncome,
      Roles.ViewMonitorPublicFixedIncome,
      Roles.ViewMonitorFutures,
      Roles.ViewMonitorMargin,
      Roles.ViewMonitorContracted,
      Roles.ViewMonitorCurrencyTerm,
      Roles.ViewMonitorVariableIncome,
      Roles.ViewMonitorSwapCetip,
      Roles.ViewMonitorExchange,
      Roles.ViewReceivable,
      Roles.ViewLiabilitiesMonitor,
      Roles.ViewMonitorOperationStructured,
      Roles.ViewStructuredOperationOrderMonitor,
    ];

    return this.authService.hasRoles(roles);
  }

  /**
   * @description Check if can change cutoff default or extend time limit
   * @returns true if user has roles
   */
  canChangeCutoff(): boolean {
    const roles = [Roles.ChangeDefaultLimitTime, Roles.ChangeLimitTime];
    return this.authService.hasRoles(roles);
  }

  /**
   * @description Check if can show security menu options
   * @returns true if user has some of the roles
   */
  canShowSecurity(): boolean {
    const roles = [
      Roles.CreateMasterUser,
      Roles.CreateSubordinateUser,
      Roles.ChangeDefaultLimitTime,
      Roles.ChangeLimitTime,
    ];

    return this.authService.hasRoles(roles);
  }

  canShowUtility(): boolean {
    const roles = [
      Roles.CreateMasterUser,
      Roles.CreateSubordinateUser,
      Roles.ChangeDefaultLimitTime,
      Roles.ChangeLimitTime,
    ];

    return this.authService.hasRoles(roles);
  }

  canShowUploadTransfer(): boolean {
    const roles = [
      Roles.UploadTransfer,
      Roles.ApproveUploadTransfer,
      Roles.ViewUploadTransferMonitor,
    ];

    return this.authService.hasRoles(roles);
  }
}
